'use client'

import { Textarea } from '@/shared/components/ui/textarea'
import { useAutoResizeTextarea } from '@/shared/hooks/use-auto-rezise-textarea'
import { cn } from '@/shared/lib/utils'
import { ArrowRight } from 'lucide-react'
import { useState } from 'react'

interface AIPromptProps {
    onSubmit: (text: string) => void
    customButton?: React.ReactNode
    placeholder: string
}

export default function AI_Prompt({ onSubmit, customButton, placeholder }: AIPromptProps) {
    const [value, setValue] = useState('')
    const { textareaRef, adjustHeight } = useAutoResizeTextarea({
        minHeight: 72,
        maxHeight: 300,
    })

    const handleSend = () => {
        const trimmed = value.trim()
        if (!trimmed) return
        onSubmit(trimmed)
        setValue('')
        adjustHeight(true)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    return (
        <div>
            <div className="bg-black/5 dark:bg-white/5 rounded-2xl p-1">
                <div className="relative">
                    <div className="relative flex flex-col">
                        <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
                            <Textarea
                                id="ai-input-15"
                                value={value}
                                placeholder={placeholder}
                                className={cn(
                                    'w-full rounded-xl rounded-b-none px-4 py-3 bg-black/5 dark:bg-white/5 border-none dark:text-white placeholder:text-black/70 dark:placeholder:text-white/70 resize-none focus-visible:ring-0 focus-visible:ring-offset-0',
                                    'min-h-[72px]'
                                )}
                                ref={textareaRef}
                                onKeyDown={handleKeyDown}
                                onChange={(e) => {
                                    setValue(e.target.value)
                                    adjustHeight()
                                }}
                            />
                        </div>

                        <div className="h-14 bg-black/5 dark:bg-white/5 rounded-b-xl flex items-center">
                            <div className="absolute left-3 right-3 bottom-3 flex items-center justify-end w-[calc(100%-24px)] space-x-2">
                                {customButton && customButton}
                                <button
                                    type="button"
                                    onClick={handleSend}
                                    className={cn(
                                        'rounded-lg p-2 bg-slate-800',
                                        'hover:bg-slate-700',
                                        'focus-visible:outline-none focus-visible:ring-2',
                                        'disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
                                    )}
                                    aria-label="Send message"
                                    disabled={!value.trim()}
                                >
                                    <ArrowRight
                                        className={cn(
                                            'w-4 h-4 text-white transition-opacity duration-200',
                                            value.trim() ? 'opacity-100' : 'opacity-50'
                                        )}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
